/**
 * Frontend service for the AI Transfer History Summary Medplum Bot.
 *
 * Invoked from the transfer modal to generate an AI clinical handoff summary
 * scoped to a specific EpisodeOfCare and the current therapist.
 *
 * Bot identifier: { system: 'https://unify.health/bots', value: 'ai-transfer-history-summary' }
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';

export const AI_TRANSFER_SUMMARY_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'ai-transfer-history-summary',
} as const;

export interface TransferSummaryBotInput {
  patientId: string;
  episodeOfCareId: string;
  currentPractitionerId: string;
  forceRegenerate?: boolean;
}

export interface TransferSummaryBotOutput {
  insufficientData: boolean;
  summary: string;
  sourceCounts: {
    encounters: number;
    notes: number;
    questionnaireResponses: number;
    treatmentPlanningDocs: number;
  };
  model: string;
  generatedAt: string;
}

export function getAiTransferSummaryBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_AI_TRANSFER_SUMMARY_BOT_ID?.trim();
  if (id) return id;
  return AI_TRANSFER_SUMMARY_BOT_IDENTIFIER;
}

export async function requestTransferHistorySummary(
  medplum: MedplumClient,
  input: TransferSummaryBotInput,
): Promise<TransferSummaryBotOutput> {
  return medplum.executeBot(
    getAiTransferSummaryBotTarget(),
    input,
    ContentType.JSON,
  ) as Promise<TransferSummaryBotOutput>;
}
